import { ArrowBack, EmailOutlined, MailOutlineOutlined } from "@mui/icons-material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Button, ButtonGroup, CircularProgress, IconButton, Popover, Tooltip } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { RefreshData } from "components/helper";
import ConfirmDialog from "components/Modals/ConfirmDialog";
import DeleteModal from "components/Modals/DeleteModal";
import WholeScreenLoader from "components/WholeScreenLoader";
import { deleteData } from "const/axios";
import { API_URL } from "const/constants";
import useKothar from "context/useKothar";
import DisplayDate from "components/DisplayDate";
import { differenceInDays } from "date-fns";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import React, { useEffect, useMemo, useState } from "react";
import { AiFillDelete } from "react-icons/ai";
import { FaCheckCircle, FaTimes } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { convertClientForm, fetchClientFormPage } from "./checkInFormListApi";

const EMPTY_CHECK_IN_FILTERS = {
  formTypeFilter: "check-in",
  convertedFilter: "not-converted",
  search: "",
  pageIndex: 0,
  pageSize: 20,
};

const FORM_TYPE_FILTERS = [
  { label: "Check-in", value: "check-in" },
  { label: "Client Details", value: "client-details" },
  // PR enquiries have their own endpoints and list (Client → PR Enquiries), not client forms.
  // { label: "PR Enquiry", value: "pr-enquiry" },
  { label: "All", value: "all" },
];

const CONVERTED_FILTERS = [
  { label: "All", value: "all" },
  { label: "Converted", value: "converted" },
  { label: "Not Converted", value: "not-converted" },
];

const CheckInData = ({ color = "light", isDashboard = false, initialFormType = "check-in" }) => {
  const navigate = useNavigate();
  const [openConfirmationModal, setOpenConfirmationModal] = useState({});
  const [{ token }] = useKothar();
  const [filters, setFilters] = useState({
    ...EMPTY_CHECK_IN_FILTERS,
    formTypeFilter: initialFormType,
  });

  // Reset to first page whenever a filter (not pagination) changes.
  useEffect(() => {
    setFilters((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }));
  }, [filters.formTypeFilter, filters.convertedFilter]);

  const pageQuery = useQuery(
    ["client-form-page", filters],
    () =>
      fetchClientFormPage({
        page: filters.pageIndex,
        pageSize: filters.pageSize,
        search: filters.search,
        type: filters.formTypeFilter,
        convertedFilter: filters.convertedFilter,
      }),
    { keepPreviousData: true, refetchOnWindowFocus: false, refetchOnMount: "always" }
  );

  const pageData = pageQuery.data ?? { content: [], totalItems: 0 };
  const filteredData = pageData.content ?? [];
  const totalItems = pageData.totalItems ?? 0;
  const listLoading = pageQuery.isFetching;

  const handleDeleteData = () => {
    deleteData(
      `${API_URL}/client-form/${openConfirmationModal?.id}`,
      () => {
        setOpenConfirmationModal({ state: false, id: null });
        pageQuery.refetch();
      },
      token
    );
  };
  // One-click Convert from the list: same conversion the Yes/No dropdown performs, minus the
  // round trip through the edit screen. Confirmed first — it creates a real client record.
  const [formPendingConversion, setFormPendingConversion] = useState(null);

  const { mutate: convertForm, isLoading: converting } = useMutation(convertClientForm, {
    onSuccess: (converted) => {
      setFormPendingConversion(null);
      pageQuery.refetch();
      toast.success(`${converted?.name || "Submission"} converted to a client`);
    },
    onError: (error) => {
      setFormPendingConversion(null);
      toast.error(
        error?.response?.data?.errorMessage ||
          error?.response?.data?.message ||
          "Could not convert this submission"
      );
    },
  });

  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const [loading, setLoading] = useState(false);

  const columns = useMemo(
    () => [
      // {
      //   accessorKey: "sn",
      //   header: "SN",
      //   size: 20,
      //   Cell: ({ row }) => {
      //     return <div>{row?.index + 1}</div>;
      //   },
      // },
      {
        accessorKey: "createdDate",
        header: "Check In Date",
        size: 150,
        Cell: ({ row }) => {
          return (
            <div>
              <DisplayDate value={row?.original?.createdDate} />
            </div>
          );
        },
      },
      {
        accessorKey: "name",
        header: "Name",
        size: 150,
        Cell: ({ row }) => {
          return <div>{row?.original?.name}</div>;
        },
      },
      {
        accessorKey: "email",
        header: "Email",
        size: 150,
      },
      {
        accessorKey: "clientConversionStatus",
        header: "Client Conversion Status",
        size: 250,
        Cell: ({ row }) => {
          const form = row?.original;
          const isConverted = Boolean(form?.clientConversionStatus);
          const isConvertingThis = converting && formPendingConversion?.id === form?.id;

          return (
            <div className="flex items-center gap-2">
              {isConverted ? (
                <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                  <FaCheckCircle className="text-green-600" />
                </div>
              ) : (
                <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                  <FaTimes className="text-red-600" />
                </div>
              )}
              <span className="whitespace-nowrap">
                {isConverted ? "Converted" : "Not Converted"}
              </span>
            </div>
          );
        },
      },
      {
        accessorKey: "number",
        header: "Number",
        size: 100,
      },
      {
        accessorKey: "address",
        header: "Address",
        size: 100,
      },
      {
        accessorKey: "nationality",
        header: "Nationality",
        size: 150,
      },
      {
        accessorKey: "visaStatus",
        header: "Visa Status",
        size: 100,
      },

      {
        accessorKey: "visaExpiry",
        header: "Visa Expiry",
        size: 150,
        Cell: ({ row }) => {
          const daysUntilExpiry = differenceInDays(new Date(row?.original?.visaExpiry), new Date());
          const isExpiringSoon = daysUntilExpiry < 90;

          return (
            <>
              {row?.original?.visaExpiry ? (
                <div>
                  <Tooltip
                    title={
                      isExpiringSoon
                        ? daysUntilExpiry > 0
                          ? `Visa expiring in ${daysUntilExpiry} days`
                          : "Visa expired, please contact Client"
                        : ""
                    }
                    arrow
                  >
                    <span
                      className={
                        " " +
                        (isExpiringSoon
                          ? "bg-red-400 text-white font-medium rounded-xl px-2 py-1"
                          : "")
                      }
                    >
                      {row?.original?.visaExpiry?.split(" ")[0] || "-"}
                    </span>
                  </Tooltip>
                  {isExpiringSoon && (
                    <Tooltip title={"Send Email to the Client"} arrow>
                      <IconButton
                        onClick={() => {
                          const mailtoLink = `https://mail.google.com/mail/?view=cm&fs=1&to=${row?.original?.email}`;
                          window.open(mailtoLink, "_blank");
                        }}
                      >
                        <MailOutlineOutlined className="text-primary" />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              ) : (
                "-"
              )}
            </>
          );
        },
      },
      {
        accessorKey: "source",
        header: "Source",
        size: 150,
      },
      {
        accessorKey: "interestedServices",
        header: "Interested Services",
        size: 150,
        Cell: ({ row }) => {
          return (
            <div>
              {row?.original?.interestedServices?.map((service) => (
                <div key={service}>{service}</div>
              ))}
            </div>
          );
        },
      },

      {
        accessorKey: "actions",
        header: "Actions",
        size: 150,
        Cell: ({ row, renderedCellValue }) => {
          const item = row.original;
          const [anchorEl, setAnchorEl] = useState(null);

          const handleClick = (event) => {
            setAnchorEl(event.currentTarget);
          };

          const handleClose = () => {
            setAnchorEl(null);
          };

          const open = Boolean(anchorEl);
          const form = row?.original;
          const isConverted = Boolean(form?.clientConversionStatus);
          const isConvertingThis = converting && formPendingConversion?.id === form?.id;

          return (
            <div className="flex items-center gap-2">
              <IconButton onClick={handleClick}>
                <MoreVertIcon className="text-app-text-secondary" />
              </IconButton>
              <Popover
                id={open ? "action-popover" : undefined}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                <div className="p-2 min-w-[200px]">
                  {item?.convertedClientId && (
                    <div
                      className="flex items-center gap-2 p-2 hover:bg-green-50 rounded cursor-pointer text-green-700"
                      onClick={() => {
                        navigate(`/admin/client/view/${item.convertedClientId}`);
                        handleClose();
                      }}
                    >
                      <FaCheckCircle className="text-green-600" />
                      <span>View Client</span>
                    </div>
                  )}
                  <div
                    className="flex items-center gap-2 p-2 hover:bg-app-surface-hover rounded cursor-pointer"
                    onClick={(event) => {
                      event.stopPropagation();
                      setFormPendingConversion(form);
                    }}
                  >
                    {isConvertingThis ? (
                      <CircularProgress color="inherit" className="text-primary" />
                    ) : (
                      <FaCheckCircle className="text-green-500 text-lg" />
                    )}
                    <span
                      disabled={converting}
                      sx={{ textTransform: "none", py: 0.15, minWidth: 0, whiteSpace: "nowrap" }}
                    >
                      Convert
                    </span>
                  </div>
                  <div
                    className="flex items-center gap-2 p-2 hover:bg-app-surface-hover rounded cursor-pointer"
                    onClick={() => {
                      const mailtoLink = `https://mail.google.com/mail/?view=cm&fs=1&to=${item?.email}`;
                      window.open(mailtoLink, "_blank");
                      handleClose();
                    }}
                  >
                    <EmailOutlined className="text-primary" />
                    <span>Send Email</span>
                  </div>
                  <div
                    className="flex items-center gap-2 p-2 hover:bg-app-surface-hover rounded cursor-pointer text-red-600"
                    onClick={() => {
                      setOpenConfirmationModal({ state: true, id: item?.id });
                      handleClose();
                    }}
                  >
                    <AiFillDelete className="text-red-600" />
                    <span>Delete Lead</span>
                  </div>
                </div>
              </Popover>
            </div>
          );
        },
      },
    ],
    [converting, formPendingConversion]
  );

  const table = useMaterialReactTable({
    columns,
    data: filteredData,
    getRowId: (row) => String(row.id),
    enableRowNumbers: true, // Enable row numbers
    // rowNumberDisplayMode: "page",
    // Sorting is server-driven (newest first) — column-header sort clicks are disabled
    // rather than re-sorting just the current page's 20 rows out of order with the server.
    manualSorting: true,
    enableSorting: false,
    initialState: {
      enableGlobalFilter: true,
      showGlobalFilter: true,
      density: "compact",
      columnPinning: {
        left: ["mrt-row-numbers", "name", "email", "clientConversionStatus"],
      },
    },

    paginationDisplayMode: "pages",

    enablePagination: true,
    enableStickyHeader: true,
    enableColumnPinning: true,
    manualPagination: true,
    manualFiltering: true,
    rowCount: totalItems,
    pageCount: Math.ceil(totalItems / filters.pageSize) || 0,
    onPaginationChange: setFilters,
    onGlobalFilterChange: (value) => {
      setFilters((prev) => ({ ...prev, search: value, pageIndex: 0 }));
    },

    muiTableContainerProps: {
      sx: {
        maxHeight: "70vh",
        minHeight: "70vh", // Set max height
        overflowY: "auto", // Enable vertical scrolling
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (event) => {
        // Check if the click was on an action button or its child
        const isActionClick =
          event.target.closest(".MuiIconButton-root") || event.target.closest(".MuiPopover-root");
        if (!isActionClick) {
          navigate(`/admin/client/check-in/view/${row.original?.id}`, {
            state: { item: row.original },
          });
        }
      },
      sx: {
        cursor: "pointer",
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.04)",
        },
      },
    }),
    state: {
      pagination: { pageIndex: filters.pageIndex, pageSize: filters.pageSize },
      globalFilter: filters.search || "",
      isLoading: listLoading,
      showProgressBars: listLoading,
      isSaving: listLoading,
    },
  });

  return (
    <div className="flex flex-wrap mt-4 dashBody">
      <div className="w-full px-4">
        <div className={"relative flex flex-col min-w-0 break-words w-full shadow-lg rounded"}>
          <div className="rounded-t mb-0 px-4 py-3 border-0">
            <div className="flex flex-wrap items-center justify-between">
              <div className="flex items-center gap-2 justify-between w-full">
                <h3 className={"font-semibold text-lg " + "text-app-text"}>
                  {!isDashboard ? (
                    <ArrowBack onClick={() => navigate(-1)} className="cursor-pointer mr-2" />
                  ) : null}
                  {filters.formTypeFilter === "client-details"
                    ? "Client Details"
                    : // : filters.formTypeFilter === "pr-enquiry"
                    // ? "PR Enquiries"
                    filters.formTypeFilter === "all"
                    ? "All Form Submissions"
                    : "Check In Data"}{" "}
                  ({totalItems}) <RefreshData refreshData={pageQuery.refetch} />
                </h3>

                <div className="flex items-center gap-3 flex-wrap">
                  <ButtonGroup size="small" variant="outlined">
                    {FORM_TYPE_FILTERS.map((f) => (
                      <Button
                        key={f.value}
                        onClick={() => setFilters((prev) => ({ ...prev, formTypeFilter: f.value }))}
                        variant={filters.formTypeFilter === f.value ? "contained" : "outlined"}
                      >
                        {f.label}
                      </Button>
                    ))}
                  </ButtonGroup>
                  <ButtonGroup size="small" variant="outlined" color="success">
                    {CONVERTED_FILTERS.map((f) => (
                      <Button
                        key={f.value}
                        onClick={() =>
                          setFilters((prev) => ({ ...prev, convertedFilter: f.value }))
                        }
                        variant={filters.convertedFilter === f.value ? "contained" : "outlined"}
                      >
                        {f.label}
                      </Button>
                    ))}
                  </ButtonGroup>
                  <Button variant="contained" color="primary">
                    View Public Form URL
                  </Button>
                </div>
              </div>
            </div>
          </div>
          <div className="block w-full overflow-x-auto">
            {loading && <WholeScreenLoader open={loading} />}
            <MaterialReactTable table={table} />
          </div>
        </div>
      </div>
      {openConfirmationModal.state && (
        <DeleteModal
          open={openConfirmationModal.state}
          item="Lead"
          handleCancel={() => setOpenConfirmationModal({ state: false, id: null })}
          handleDelete={() => handleDeleteData()}
        />
      )}
      <ConfirmDialog
        open={formPendingConversion != null}
        title="Convert to client?"
        message={`${
          formPendingConversion?.name || "This submission"
        } will be added to your clients using the details on this submission${
          formPendingConversion?.email ? ` (${formPendingConversion.email})` : ""
        }. If they are already a client, this links to the existing record instead of creating a second one.`}
        confirmLabel="Convert"
        confirmColor="success"
        loading={converting}
        onConfirm={() => convertForm(formPendingConversion?.id)}
        onCancel={() => setFormPendingConversion(null)}
      />
    </div>
  );
};

export default CheckInData;
